import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiParticipant } from '@api/model/api-participant';
import { convertApiParticipant } from '@api/util/api-participant.util';
import { Lap } from '@model/lap';
import { Participant } from '@model/participant';
import { BaseSearchService } from '@shared/service/base-search.service';
import { Team } from '@team/team';
import { enrichParticipant } from '@util/participant-util';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

const convertApiParticipants = (
  apiParticipants: ApiParticipant[],
): Participant[] =>
  apiParticipants
    .map(apiParticipant => convertApiParticipant(apiParticipant))
    .map(participant => enrichParticipant(participant));

@Injectable({ providedIn: 'root' })
export class ParticipantService extends BaseSearchService<Participant> {
  constructor() {
    super('participants');
  }

  getParticipant(uuid: string): Observable<Participant> {
    const url = `${this.baseUrl}/${uuid}`;

    return this.httpClient
      .get<Participant>(url)
      .pipe(map(participant => enrichParticipant(participant)));
  }

  getParticipants(uuids: string[]): Observable<Participant[] | undefined> {
    if (uuids.length === 0) {
      return of([]);
    }

    const getParticipants$ = uuids.map(uuid => this.getParticipant(uuid));
    return forkJoin(getParticipants$).pipe(catchError(() => of(undefined)));
  }

  getParticipantLaps(participant: Participant): Observable<Lap[]> {
    const url = `${this.baseUrl}/${participant.uuid}/laps`;

    return this.httpClient.get<Lap[]>(url).pipe(catchError(() => of([])));
  }

  /** TODO (backend, ALPE6-519): can we create a seperate laps for participants endpoint? data is all laps, live event or not */
  getParticipantsLaps(
    participants: Participant[],
  ): Observable<Map<string, Lap[]>> {
    const participantsLaps$ = participants.map(participant =>
      this.getParticipantLaps(participant),
    );

    return forkJoin(participantsLaps$).pipe(
      map(result =>
        result.reduce((acc, participantLaps, index) => {
          const participant = participants.at(index);
          if (participant) {
            acc.set(participant.uuid, participantLaps);
          }
          return acc;
        }, new Map<string, Lap[]>()),
      ),
    );
  }

  getLiveLaps(participants: Participant[]): Observable<Lap[]> {
    const url = `${this.baseUrl}/laps`;

    const params: HttpParams = participants.reduce((acc, participant) => {
      acc = acc.append('uuid', participant.uuid);

      return acc;
    }, new HttpParams());

    // TODO: error handling on backend call. Always return []? Or return prev?
    // This endpoint returns an array of lap arrays, one array per participant
    return this.httpClient.get<Lap[][]>(url, { params }).pipe(
      map(lapsList => lapsList.flat()),
      catchError(() => of([])),
    );
  }

  getParticipantsOfTeam(team: Team): Observable<Participant[]> {
    return this.httpClient
      .get<ApiParticipant[]>(`${this.baseUrl}/team/${team.uuid}`)
      .pipe(
        map(apiParticipants => convertApiParticipants(apiParticipants))
      );
  }

  override search(query: string): Observable<Participant[]> {
    return super
      .search(query)
      .pipe(
        map(apiParticipants => convertApiParticipants(apiParticipants))
      );
  }
}
